import { Upload } from "@carbon/icons-react";
import {
  Button,
  Layer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from "@carbon/react";
import { ModalTransition, SingleSelectAppliedFilter } from "@flaminem/ui-components";
import React, { ChangeEvent, useEffect, useState } from "react";

import { AuthorizedItem } from "src/authentication/AuthorizedMenu";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider";
import { AppliedFilters } from "src/components/Filters/AppliedFilters.tsx";
import ImportModal from "src/features/import/ImportModal";
import { debounceSearchTime } from "src/hooks/useSearch.ts";
import { useT } from "src/internationalization";
import { CasesFilter } from "src/pages/Cases/CasesFilter";
import { useCaseTypes } from "src/providers/CaseTypesProvider";
import { debounce } from "src/utils/functions.utils.ts";

type CasesToolbarProps = {
  searchTerm: string;
  onlyScored: boolean;
  caseTypeCode: string;
  handleOnSearchTermChange: (value: string) => void;
  handleOnFiltersChange: (filters: { onlyScored: boolean }) => void;
  placeholder?: string;
};

export const CasesToolbar: React.FC<CasesToolbarProps> = ({
  searchTerm,
  onlyScored,
  handleOnSearchTermChange,
  handleOnFiltersChange,
  caseTypeCode,
  placeholder,
}) => {
  const t = useT();
  const [currentSearchTerm, setCurrentSearchTerm] = useState(searchTerm);
  const [showImportListsModal, setShowImportListsModal] = useState(false);
  const { caseTypes } = useCaseTypes();

  useEffect(() => {
    setCurrentSearchTerm(searchTerm);
  }, [searchTerm]);

  const debouncedSearch = React.useMemo(() => {
    return debounce(handleOnSearchTermChange, debounceSearchTime);
    // We don't want to re-create the debounced function on every render, it makes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSearchTerm = (value: string) => {
    setCurrentSearchTerm(value);
    debouncedSearch(value);
  };

  const handleSearchInputChange = (event: "" | ChangeEvent<HTMLInputElement>) => {
    if (!event) return;
    setSearchTerm(event.target.value);
  };

  const onResetFilter = () => {
    handleOnFiltersChange({ onlyScored: false });
  };

  const caseType = caseTypes.find((caseType) => caseType.code === caseTypeCode);

  return (
    <>
      <TableToolbar aria-label="data table toolbar">
        <TableToolbarContent>
          <TableToolbarSearch
            placeholder={placeholder || t("page.cases.tab.requests.table.search.placeholder")}
            onChange={handleSearchInputChange}
            onClear={() => setSearchTerm("")}
            persistent={true}
            value={currentSearchTerm}
          />
          <AuthorizedItem
            requiredPermissions={[{ resource: ScopeResource.CASE, scopes: [Scope.IMPORT] }]}
          >
            <Button renderIcon={Upload} onClick={() => setShowImportListsModal(true)}>
              {t("import.button.import")}
            </Button>
          </AuthorizedItem>
          <CasesFilter
            values={{ onlyScored }}
            onApplyFilter={handleOnFiltersChange}
            onResetFilter={() => handleOnFiltersChange({ onlyScored: false })}
          />
        </TableToolbarContent>
      </TableToolbar>
      {onlyScored && (
        <div className="flex w-full bg-layer">
          <Layer className="p-5">
            <AppliedFilters onClear={onResetFilter}>
              <SingleSelectAppliedFilter
                item={{
                  label: t("page.cases.tab.requests.table.filter.scored.value"),
                  value: "true",
                }}
                title={t("page.cases.tab.requests.table.filter.scored.title")}
                onClear={onResetFilter}
              />
            </AppliedFilters>
          </Layer>
        </div>
      )}
      <ModalTransition>
        {showImportListsModal && caseType && (
          <ImportModal
            importSpecType={"case"}
            onClose={() => {
              setShowImportListsModal(false);
            }}
            caseType={caseType}
          />
        )}
      </ModalTransition>
    </>
  );
};
